// src/pages/Admin/ExamRegistrationsAdmin.jsx
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import {
  ArrowDownTrayIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../supabaseClient';

const SEGMENTS = ['Deleted', 'Active', 'All'];

// ---------- Reads ----------
async function fetchCounts() {
  const { count: activeCount, error: activeErr } = await supabase
    .from('student_exam_session')
    .select('id', { count: 'exact', head: true })
    .eq('is_deleted', false);
  if (activeErr) throw activeErr;

  const { count: deletedCount, error: deletedErr } = await supabase
    .from('student_exam_session')
    .select('id', { count: 'exact', head: true })
    .eq('is_deleted', true);
  if (deletedErr) throw deletedErr;

  return { active: activeCount ?? 0, deleted: deletedCount ?? 0 };
}

/**
 * Exam registrations search (mirrors PaymentsAdmin approach):
 * - If no term → single LEFT-join query.
 * - If term → merge base search (venue/remarks) + INNER joins on students/exam_sessions.
 */
async function fetchRegistrations({ segment, q }) {
  const term = (q || '').trim();

  const baseFields = `
    id,
    student_id,
    exam_session_id,
    registration_status,
    venue,
    total_fee_paid,
    payment_date,
    is_deleted,
    remarks,
    created_at,
    updated_at,
    students:students ( id, full_name, email ),
    exam_sessions:exam_sessions ( id, name, start_date, end_date ),
    student_exam_info ( id )
  `;

  const applySegment = (query) => {
    if (segment === 'Active') return query.eq('is_deleted', false);
    if (segment === 'Deleted') return query.eq('is_deleted', true);
    return query; // All
  };

  // No search term: one query
  if (!term) {
    let q1 = supabase
      .from('student_exam_session')
      .select(baseFields)
      .order('updated_at', { ascending: false })
      .order('created_at', { ascending: false });
    q1 = applySegment(q1);
    const { data, error } = await q1;
    if (error) throw error;

    return (data || []).map((r) => ({
      ...r,
      student_name: r?.students?.full_name ?? null,
      student_email: r?.students?.email ?? null,
      session_name: r?.exam_sessions?.name ?? null,
      subjects_count: Array.isArray(r?.student_exam_info) ? r.student_exam_info.length : 0,
    }));
  }

  // With term: run multiple queries and merge
  // A) Base-only search on venue/remarks (LEFT join)
  let baseQuery = supabase
    .from('student_exam_session')
    .select(baseFields)
    .or(`venue.ilike.%${term}%,remarks.ilike.%${term}%`)
    .order('updated_at', { ascending: false })
    .order('created_at', { ascending: false });
  baseQuery = applySegment(baseQuery);
  const { data: baseData, error: baseErr } = await baseQuery;
  if (baseErr) throw baseErr;

  // B) Student full_name (INNER join)
  let studentQuery = supabase
    .from('student_exam_session')
    .select(
      baseFields.replace(
        'students:students ( id, full_name, email )',
        'students:students!inner ( id, full_name, email )'
      )
    )
    .or(`full_name.ilike.%${term}%`, { foreignTable: 'students' })
    .order('updated_at', { ascending: false })
    .order('created_at', { ascending: false });
  studentQuery = applySegment(studentQuery);
  const { data: studentData, error: studentErr } = await studentQuery;
  if (studentErr) throw studentErr;

  // C) Session name (INNER join)
  let sessionQuery = supabase
    .from('student_exam_session')
    .select(
      baseFields.replace(
        'exam_sessions:exam_sessions ( id, name, start_date, end_date )',
        'exam_sessions:exam_sessions!inner ( id, name, start_date, end_date )'
      )
    )
    .or(`name.ilike.%${term}%`, { foreignTable: 'exam_sessions' })
    .order('updated_at', { ascending: false })
    .order('created_at', { ascending: false });
  sessionQuery = applySegment(sessionQuery);
  const { data: sessionData, error: sessionErr } = await sessionQuery;
  if (sessionErr) throw sessionErr;

  // Merge
  const map = new Map();
  (baseData || []).forEach((r) => map.set(r.id, r));
  (studentData || []).forEach((r) => {
    if (!map.has(r.id)) map.set(r.id, r);
  });
  (sessionData || []).forEach((r) => {
    if (!map.has(r.id)) map.set(r.id, r);
  });

  const merged = Array.from(map.values());
  return merged.map((r) => ({
    ...r,
    student_name: r?.students?.full_name ?? null,
    student_email: r?.students?.email ?? null,
    session_name: r?.exam_sessions?.name ?? null,
    subjects_count: Array.isArray(r?.student_exam_info) ? r.student_exam_info.length : 0,
  }));
}

// ---- Detail reads (drawer) ----
async function fetchRegistrationDetails(id) {
  const { data, error } = await supabase
    .from('student_exam_session')
    .select(`
      id,
      student_id,
      exam_session_id,
      registration_status,
      venue,
      total_fee_paid,
      payment_date,
      is_deleted,
      remarks,
      created_at,
      updated_at,
      students:students ( id, full_name, email, contact_no ),
      exam_sessions:exam_sessions ( id, name, start_date, end_date )
    `)
    .eq('id', id)
    .single();
  if (error) throw error;

  return {
    ...data,
    student_name: data?.students?.full_name ?? null,
    student_email: data?.students?.email ?? null,
    student_contact: data?.students?.contact_no ?? null,
    session_name: data?.exam_sessions?.name ?? null,
    session_dates:
      data?.exam_sessions?.start_date || data?.exam_sessions?.end_date
        ? `${new Date(data.exam_sessions.start_date).toLocaleDateString()} – ${new Date(
            data.exam_sessions.end_date
          ).toLocaleDateString()}`
        : '—',
  };
}

async function fetchRegistrationSubjects(id) {
  const { data, error } = await supabase
    .from('student_exam_info')
    .select(`
      id,
      session_registration_id,
      subject_id,
      attempt_no,
      registration_status,
      result_status,
      notes,
      created_at,
      subjects:subjects ( code, name )
    `)
    .eq('session_registration_id', id)
    .order('created_at', { ascending: true });
  if (error) throw error;

  // Sort by subject code (numeric) then name
  const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
  return (data || [])
    .map((r) => ({
      id: r.id,
      code: r?.subjects?.code ?? '',
      name: r?.subjects?.name ?? '',
      attempt_no: r.attempt_no ?? 1,
      reg_status: r.registration_status ?? 'Registered',
      res_status: r.result_status ?? 'Pending',
      notes: r.notes ?? '',
      created_at: r.created_at,
    }))
    .sort((a, b) => {
      const anum = parseInt((a.code || '').replace(/\D+/g, ''), 10);
      const bnum = parseInt((b.code || '').replace(/\D+/g, ''), 10);
      if (!Number.isNaN(anum) && !Number.isNaN(bnum) && anum !== bnum) return anum - bnum;
      return collator.compare((a.code || '').trim(), (b.code || '').trim());
    });
}

// ---------- Writes ----------
async function restoreRegistrationsByIds(ids) {
  // restore parent
  const { error: pErr } = await supabase.from('student_exam_session').update({ is_deleted: false }).in('id', ids);
  if (pErr) throw pErr;
  // restore children linked to those parents
  const { error: cErr } = await supabase
    .from('student_exam_info')
    .update({ is_deleted: false })
    .in('session_registration_id', ids);
  if (cErr) throw cErr;
  return { restored: ids.length };
}

async function hardDeleteRegistrationsByIds(ids) {
  // delete children first
  const { error: cErr } = await supabase.from('student_exam_info').delete().in('session_registration_id', ids);
  if (cErr) throw cErr;
  // then delete parents
  const { error: pErr } = await supabase.from('student_exam_session').delete().in('id', ids);
  if (pErr) throw pErr;
  return { deleted: ids.length };
}

// ---- CSV helper ----
function toCSV(rows) {
  const headers = [
    'student_name',
    'student_email',
    'session_name',
    'registration_status',
    'venue',
    'total_fee_paid',
    'payment_date',
    'is_deleted',
    'subjects_count',
    'created_at',
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  rows.forEach((r) => {
    lines.push(
      [
        escape(r.student_name),
        escape(r.student_email),
        escape(r.session_name),
        escape(r.registration_status),
        escape(r.venue),
        escape(r.total_fee_paid ?? ''),
        escape(
          r.payment_date
            ? new Date(r.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : ''
        ),
        escape(r.is_deleted ? 'true' : 'false'),
        escape(r.subjects_count ?? 0),
        escape(r.created_at ? new Date(r.created_at).toISOString() : ''),
      ].join(',')
    )
  });
  return new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export default function ExamRegistrationsAdmin() {
  const qc = useQueryClient();

  const [segment, setSegment] = useState('Deleted');
  const [selection, setSelection] = useState(new Set());
  const [search, setSearch] = useState('');

  // Drawer
  const [drawerId, setDrawerId] = useState(null);
  const [drawerTab, setDrawerTab] = useState('Summary');

  // Modals
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreIds, setRestoreIds] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIds, setDeleteIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState('');

  // Counts
  const { data: counts = { active: 0, deleted: 0 } } = useQuery({
    queryKey: ['admin_exam_regs_counts'],
    queryFn: fetchCounts,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  // List (live)
  const { data: rows = [], isLoading, isFetching } = useQuery({
    queryKey: ['admin_exam_regs_list', segment, search],
    queryFn: () => fetchRegistrations({ segment, q: search }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  // Details
  const { data: detail } = useQuery({
    queryKey: ['admin_exam_reg_detail', drawerId],
    queryFn: () => fetchRegistrationDetails(drawerId),
    enabled: !!drawerId,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
  const { data: detailSubjects = [] } = useQuery({
    queryKey: ['admin_exam_reg_subjects', drawerId],
    queryFn: () => fetchRegistrationSubjects(drawerId),
    enabled: !!drawerId && drawerTab === 'Subjects',
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  // Restore
  const restoreMutation = useMutation({
    mutationFn: (ids) => restoreRegistrationsByIds(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin_exam_regs_list'] });
      qc.invalidateQueries({ queryKey: ['admin_exam_regs_counts'] });
      qc.invalidateQueries({ queryKey: ['exam_registrations_list'] }); // if you have one elsewhere
      setShowRestoreModal(false);
      setRestoreIds([]);
      setSelection(new Set());
      if (drawerId && restoreIds.includes(drawerId)) {
        qc.invalidateQueries({ queryKey: ['admin_exam_reg_detail', drawerId] });
      }
    },
    onError: (err) => alert(`Failed to restore: ${err?.message || err}`),
  });

  // Hard delete
  const hardDeleteMutation = useMutation({
    mutationFn: (ids) => hardDeleteRegistrationsByIds(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin_exam_regs_list'] });
      qc.invalidateQueries({ queryKey: ['admin_exam_regs_counts'] });
      setShowDeleteModal(false);
      setDeleteIds([]);
      setConfirmDelete('');
      setSelection(new Set());
      if (drawerId && deleteIds.includes(drawerId)) setDrawerId(null);
    },
    onError: (err) => alert(`Failed to delete: ${err?.message || err}`),
  });

  const columns = useMemo(
    () => [
      { key: 'student', label: 'Student' },
      { key: 'session', label: 'Session' },
      { key: 'status', label: 'Reg. Status' },
      { key: 'venue', label: 'Venue' },
      { key: 'fee', label: 'Fee Paid' },
      { key: 'payment_date', label: 'Payment Date' },
      { key: 'subjects', label: 'Subjects' },
      { key: 'deleted', label: 'Deleted?' },
      { key: 'actions', label: 'Actions' },
    ],
    []
  );

  const allSelected = selection.size > 0 && selection.size === rows.length;
  const anyDeletedSelected = useMemo(
    () => rows.some((r) => selection.has(r.id) && r.is_deleted),
    [rows, selection]
  );

  const openDrawer = (id) => {
    setDrawerId(id);
    setDrawerTab('Summary');
  };

  const onExportCSV = () => {
    if (!rows || rows.length === 0) return;
    const blob = toCSV(rows);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const suffix = segment === 'All' ? 'all' : segment === 'Active' ? 'active' : 'deleted';
    downloadBlob(blob, `exam-registrations-${suffix}-${stamp}.csv`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Exam Registrations Master Administration</h1>
          <p className="text-sm text-gray-500">Review, restore, permanently delete, or export exam registrations.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="flex items-center gap-2"
            onClick={onExportCSV}
            disabled={isLoading || (rows?.length ?? 0) === 0}
            title={rows.length ? 'Export current rows to CSV' : 'Nothing to export'}
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-semibold text-gray-900">{counts.active}</div>
            </div>
            <div className="text-green-600">
              <CheckCircleIcon className="h-6 w-6" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Deleted</div>
              <div className="text-2xl font-semibold text-gray-900">{counts.deleted}</div>
            </div>
            <div className="text-gray-600">
              <XCircleIcon className="h-8 w-8 text-rose-500 opacity-80" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filter Bar */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="inline-flex rounded-md shadow-sm border border-gray-200 overflow-hidden">
            {SEGMENTS.map((s) => (
              <button
                key={s}
                onClick={() => {
                  setSegment(s);
                  setSelection(new Set());
                }}
                className={`px-3 py-1.5 text-sm ${
                  segment === s ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search student / session / venue / remarks"
            className="ml-3 flex-1 max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>
      </Card>

      {/* Bulk actions */}
      {selection.size > 0 && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">{selection.size} selected</div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                disabled={!anyDeletedSelected}
                onClick={() => {
                  const ids = rows.filter((r) => selection.has(r.id) && r.is_deleted).map((r) => r.id);
                  setRestoreIds(ids);
                  setShowRestoreModal(true);
                }}
              >
                Restore
              </Button>
              <Button
                variant="destructive"
                disabled={!anyDeletedSelected}
                onClick={() => {
                  const ids = rows.filter((r) => selection.has(r.id) && r.is_deleted).map((r) => r.id);
                  setDeleteIds(ids);
                  setShowDeleteModal(true);
                }}
              >
                Hard Delete
              </Button>
              <Button variant="ghost" onClick={() => setSelection(new Set())}>
                Clear
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Results */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selection.size > 0 && selection.size === rows.length}
                    onChange={(e) => {
                      if (e.target.checked) setSelection(new Set(rows.map((r) => r.id)));
                      else setSelection(new Set());
                    }}
                  />
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-6 py-12 text-center text-gray-500">
                    Loading exam registrations…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-6 py-12 text-center text-gray-500">
                    No exam registrations to display.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const selected = selection.has(r.id);
                  return (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={selected}
                          onChange={(e) => {
                            const next = new Set(selection);
                            if (e.target.checked) next.add(r.id);
                            else next.delete(r.id);
                            setSelection(next);
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{r.student_name || '—'}</div>
                        {r.student_email && <div className="text-xs text-gray-500">{r.student_email}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.session_name || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {r.registration_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.venue || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {r.total_fee_paid == null ? '—' : `RM${Number(r.total_fee_paid).toFixed(2)}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {r.payment_date
                          ? new Date(r.payment_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.subjects_count}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {r.is_deleted ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-50 text-green-700">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-3">
                          <button className="text-blue-600 hover:text-blue-800" onClick={() => openDrawer(r.id)}>
                            Details
                          </button>
                          {r.is_deleted ? (
                            <>
                              <button
                                className="text-indigo-600 hover:text-indigo-800"
                                onClick={() => {
                                  setRestoreIds([r.id]);
                                  setShowRestoreModal(true);
                                }}
                              >
                                Restore
                              </button>
                              <button
                                className="text-red-600 hover:text-red-800"
                                onClick={() => {
                                  setDeleteIds([r.id]);
                                  setShowDeleteModal(true);
                                }}
                              >
                                Delete
                              </button>
                            </>
                          ) : (
                            <button className="text-gray-400 cursor-not-allowed" disabled>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {isFetching && rows.length > 0 && <div className="px-4 py-2 text-xs text-gray-400">Refreshing…</div>}
      </Card>

      {/* ----- Drawer (Details) ----- */}
      {drawerId && (
        <div className="fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/30" onClick={() => setDrawerId(null)} />
          <div className="ml-auto h-full w-full max-w-xl bg-white shadow-xl z-50 flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-xs text-gray-500">Student</div>
                <h2 className="text-lg font-semibold truncate">{detail?.student_name || '—'}</h2>
                <div className="text-sm text-gray-500 truncate">
                  {detail?.session_name || '—'} {detail?.session_dates ? `· ${detail.session_dates}` : ''}
                </div>
              </div>
              <button onClick={() => setDrawerId(null)}>
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* Drawer tabs */}
            <div className="px-4 pt-3">
              <div className="inline-flex rounded-md shadow-sm border border-gray-200 overflow-hidden">
                {['Summary', 'Subjects'].map((t) => (
                  <button
                    key={t}
                    onClick={() => setDrawerTab(t)}
                    className={`px-3 py-1.5 text-sm ${
                      drawerTab === t ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 space-y-6 overflow-y-auto">
              {/* Summary */}
              {drawerTab === 'Summary' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-gray-500">Registration Status</div>
                      <div className="text-sm text-gray-900">{detail?.registration_status || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Venue</div>
                      <div className="text-sm text-gray-900">{detail?.venue || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Fee Paid</div>
                      <div className="text-sm text-gray-900">
                        {detail?.total_fee_paid == null ? '—' : `RM${Number(detail.total_fee_paid).toFixed(2)}`}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Payment Date</div>
                      <div className="text-sm text-gray-900">
                        {detail?.payment_date
                          ? new Date(detail.payment_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Created</div>
                      <div className="text-sm text-gray-900">
                        {detail?.created_at ? new Date(detail.created_at).toLocaleString() : '—'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500">Remarks</div>
                    <div className="text-sm text-gray-900 whitespace-pre-wrap">{detail?.remarks || '—'}</div>
                  </div>
                </div>
              )}

              {/* Subjects */}
              {drawerTab === 'Subjects' && (
                <Card>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Attempt</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reg. Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {detailSubjects.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                              No subjects for this registration.
                            </td>
                          </tr>
                        ) : (
                          detailSubjects.map((s) => (
                            <tr key={s.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {s.code ? `${s.code} — ${s.name}` : s.name || '—'}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">{s.attempt_no}</td>
                              <td className="px-4 py-2 text-sm">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                  {s.reg_status}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                  {s.res_status}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-pre-wrap">{s.notes || '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>

            {/* Drawer footer */}
            <div className="p-4 border-t flex items-center justify-end gap-2">
              {detail?.is_deleted ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setRestoreIds([drawerId]);
                      setShowRestoreModal(true);
                    }}
                  >
                    Restore
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setDeleteIds([drawerId]);
                      setShowDeleteModal(true);
                    }}
                  >
                    Hard Delete
                  </Button>
                </>
              ) : (
                <span className="text-xs text-gray-400">Active registration</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={() => setShowRestoreModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">Restore registration{restoreIds.length > 1 ? 's' : ''}?</h3>
            <p className="text-sm text-gray-600 mb-4">
              {restoreIds.length > 1
                ? `You are about to restore ${restoreIds.length} registrations.`
                : 'This registration and its subjects will reappear in lists.'}
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRestoreModal(false)}>Cancel</Button>
              <Button onClick={() => restoreMutation.mutate(restoreIds)} disabled={restoreMutation.isLoading}>
                {restoreMutation.isLoading ? 'Restoring…' : 'Restore'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hard Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold">Permanently delete registration{deleteIds.length > 1 ? 's' : ''}?</h3>
                <p className="text-sm text-gray-600">
                  All subject attempts under these registrations will be deleted first.
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <label className="text-sm text-gray-700">
              Type <span className="font-mono">DELETE</span> to confirm
            </label>
            <input
              className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
              placeholder="DELETE"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={confirmDelete !== 'DELETE' || hardDeleteMutation.isLoading}
                onClick={() => hardDeleteMutation.mutate(deleteIds)}
              >
                {hardDeleteMutation.isLoading ? 'Deleting…' : 'Hard Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
